import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Bell, ChevronDown, Search, Sparkles, LayoutGrid, Inbox, Settings2 } from "lucide-react";
import { Logo } from "@/components/logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { ROLES, STATIONS } from "@/lib/mock";
import { SurecRayi } from "@/components/surec-rayi";
import { cn } from "@/lib/cn";
import { useOturum } from "@/lib/oturum";
import { cevir } from "@/lib/dil";


export function ShellB({ children }: { children: React.ReactNode }) {
  const [role, setRole] = useState(ROLES[0]!);
  const { ben, cikis } = useOturum();
  const [aktif, setAktif] = useState<string | null>(null);

  return (
    <div className="font-manrope min-h-dvh bg-bg text-ink">
      {/* Üst çubuk */}
      <header className="sticky top-0 z-30 border-b border-line bg-surface/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1440px] items-center gap-3 px-4 sm:px-6">
          <Link to="/"><Logo height={22} /></Link>
          <nav className="ml-4 hidden items-center gap-1 md:flex">
            {[
              { ad: cevir("Today"), icon: Sparkles, on: true },
              { ad: "Riser'lar", icon: LayoutGrid },
              { ad: cevir("My work"), icon: Inbox, n: 6 },
              { ad: cevir("Registers"), icon: Settings2 },
            ].map((t) => (
              <a key={t.ad} href="#" className={cn("flex items-center gap-1.5 rounded-full px-3 py-1.5 text-base font-semibold transition", t.on ? "bg-navy text-white dark:text-[#0a141c]" : "text-ink-2 hover:bg-surface-2")}>
                <t.icon size={14} /> {t.ad}
                {t.n ? <span className={cn("rounded-full px-1.5 text-2xs tnum", t.on ? "bg-white/20" : "bg-orange text-white")}>{t.n}</span> : null}
              </a>
            ))}
          </nav>
          <button className="ml-auto flex h-9 items-center gap-2 rounded-full border border-line bg-bg px-3 text-base text-ink-3 hover:border-line-strong sm:w-[260px]">
            <Search size={15} /><span className="hidden flex-1 text-left sm:inline">{cevir("Riser, SN, work order…")}</span>
          </button>
          <button className="relative rounded-full p-2 text-ink-2 hover:bg-surface-2" aria-label={cevir("Notifications")}>
            <Bell size={18} /><span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-orange ring-2 ring-surface" />
          </button>
          <ThemeToggle className="rounded-full" />
          <div className="group relative">
            <button className="flex h-9 items-center gap-2 rounded-full border border-line bg-surface pl-1 pr-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-orange text-xs font-extrabold text-white">{role.ad.split(" ").map((s) => s[0]).join("").slice(0, 2)}</span>
              <span className="hidden text-sm font-semibold sm:inline">{role.ad.split(" ")[0]}</span>
              <ChevronDown size={14} className="text-ink-3" />
            </button>
            <div className="invisible absolute right-0 top-full z-40 mt-1 w-60 rounded-xl border border-line bg-surface p-1 opacity-0 shadow-[var(--shadow-lg)] transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              {ben && (
                <div className="border-b border-line px-3 py-2">
                  <div className="text-sm font-semibold">{ben.ad}</div>
                  <div className="text-xs text-ink-3">{ben.eposta} · {cevir(ben.rolEtiket)}</div>
                  <button onClick={() => void cikis()} className="mt-2 w-full rounded-lg border border-line px-2 py-1 text-sm text-ink-2 hover:bg-surface-2">{cevir("Log out")}</button>
                </div>
              )}
              {ROLES.map((r) => (
                <button key={r.id} onClick={() => setRole(r)} className={cn("flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-base hover:bg-surface-2", r.id === role.id && "bg-surface-2 font-semibold")}>
                  {r.ad}<span className="text-xs text-ink-3">{r.kisa}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Süreç rayı */}
        <div className="mx-auto max-w-[1440px] px-4 pb-3 sm:px-6">
          <SurecRayi aktif={aktif} onSec={setAktif} />
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1440px] px-4 pb-24 pt-5 sm:px-6 md:pb-10">
        {aktif ? (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-orange/40 bg-orange-soft px-4 py-2.5 text-base">
            <span className="font-bold">{aktif} · {STATIONS.find((s) => s.code === aktif)?.ad}</span>
            <span className="text-ink-2">{cevir("queue opens: components at this station, gate status and upload points.")}</span>
            <button onClick={() => setAktif(null)} className="ml-auto text-sm font-semibold text-orange">{cevir("Close")}</button>
          </div>
        ) : null}
        {children}
      </main>

      {/* Mobil alt çubuk */}
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-line bg-surface/95 backdrop-blur md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {[
          { ad: cevir("Today"), icon: Sparkles, on: true },
          { ad: "Riser'lar", icon: LayoutGrid },
          { ad: cevir("My work"), icon: Inbox, n: 6 },
          { ad: cevir("Search"), icon: Search },
        ].map((t) => (
          <button key={t.ad} className={cn("relative flex flex-col items-center gap-0.5 py-2 text-2xs font-semibold", t.on ? "text-orange" : "text-ink-3")}>
            <t.icon size={20} />{t.ad}
            {t.n ? <span className="absolute right-[22%] top-1 rounded-full bg-orange px-1 text-2xs font-bold text-white">{t.n}</span> : null}
          </button>
        ))}
      </nav>
    </div>
  );
}
